<script setup lang="ts">
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { computed } from "vue";
import {
  CalendarRangeIcon,
  CalendarCogIcon,
  PlayIcon,
} from '@lucide/vue'
import {
  getBackgroundColor as getEquipmentBackgroundColor,
  getColor as getEquipmentColor
} from "@/utils/equipment-status";
import {
  getBackgroundColor as getBookingBackgroundColor,
  getColor as getBookingColor
} from "@/utils/booking-status";
import { getHistoryCalendarPosition, HistoryType, type History } from "@/utils/history";

dayjs.extend(utc);
dayjs.extend(timezone);

type ColorizedHistory = History & {
  /** Optional explicit primary color (overrides domain helpers) */
  backgroundColor?: string;
  iconBackgroundColor: string;
};

const props = defineProps<{
  history: ColorizedHistory,
  timezone: string,
  gridRow: number,
  columnOffset: number,
  calendarStart: dayjs.Dayjs,
  calendarEnd: dayjs.Dayjs,
}>();

const STRIPES_BG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='4' height='4' viewBox='0 0 4 4'%3E%3Cpath fill='%23ffffff' fill-opacity='1' d='M1 3h1v1H1V3zm2-2h1v1H3V1z'%3E%3C/path%3E%3C/svg%3E")`;

const backgroundColor = computed(() => {
  if (props.history.backgroundColor) return props.history.backgroundColor;
  if (props.history.type === HistoryType.Allotment) {
    return getEquipmentBackgroundColor(props.history.resource.equipment_status);
  }
  if (props.history.type === HistoryType.Booking) {
    return getBookingBackgroundColor(props.history.resource.status);
  }
  return "#BFD4FF" // "#4770FF";
});

const iconBackgroundColor = computed(() => {
  if (props.history.iconBackgroundColor) return props.history.iconBackgroundColor;
  if (props.history.type === HistoryType.Allotment) {
    return getEquipmentColor(props.history.resource.equipment_status);
  }
  if (props.history.type === HistoryType.Booking) {
    return getBookingColor(props.history.resource.status);
  }
  return "#4770FF";
});

const historyTitle = computed(() => {
  let title: string | null = null;
  if (!props.history) return "";
  if (props.history.type === HistoryType.Allotment) {
    title = props.history.resource.title;
  }
  if (props.history.type === HistoryType.Booking) {
    title = (props.history.resource.registration?.firstname || "") + " " + (props.history.resource.registration?.lastname || "");
  }
  if (props.history.type === HistoryType.ProductTest) {
    title = props.history.resource.registration.firstname + " " + props.history.resource.registration.lastname;
  }
  return title || String(props.history.type);
});

const alignSelf = computed(() => ({
  [HistoryType.Booking]: "start",
  [HistoryType.Allotment]: "center",
  [HistoryType.ProductTest]: "end"
} as Record<HistoryType, string>)[props.history.type] ?? "center");

const zIndex = computed(() => ({
  [HistoryType.Booking]: 12,
  [HistoryType.Allotment]: 10,
  [HistoryType.ProductTest]: 13
} as Record<HistoryType, number>)[props.history.type] ?? 13);

/** Visible [start, end] within the calendar grid (1-based days) */
const position = computed(() => {
  return getHistoryCalendarPosition(
    props.history.start_at ? dayjs(props.history.start_at) : null,
    props.history.end_at ? dayjs(props.history.end_at) : null,
    props.calendarStart,
    props.calendarEnd
  );
});

/** Today's position inside the current calendar window, 0 if before window */
const currentDatePosition = computed(() => {
  const now = dayjs().tz(props.timezone).startOf("day");
  if (now.isBefore(props.calendarStart)) return 0;
  if (now.isAfter(props.calendarEnd)) return props.calendarStart.endOf("month").date() + 1;
  return now.date();
});

/** Split logic (today within [start, end]) */
const shouldSplit = computed(() => {
  if (props.history.type !== HistoryType.ProductTest) return false;
  const { end } = position.value;
  const cur = currentDatePosition.value;
  return cur <= end;
});

/** Total columns inside the item block */
const totalCols = computed(() => {
  const { start, end } = position.value;
  if (!start || !end) return 0;
  return Math.max(0, end - start + 1);
});

/** Grid placement on the outer month grid */
const outerGridColumn = computed(() => {
  const startDay = position.value.start;
  const endDay = position.value.end;
  return (startDay + props.columnOffset) + " / span " + (endDay - startDay + 1);
});

/** Styles */
const buttonStyle = computed(() => ({
  gridColumn: outerGridColumn.value,
  gridRow: String(props.gridRow),
  alignSelf: alignSelf.value,
  zIndex: String(zIndex.value),
  // color: textColor.value,
  background: backgroundColor.value,
} as Record<string, string>));

const bgGridStyle = computed(() => ({
  gridTemplateColumns: `repeat(${totalCols.value}, 1fr)`,
} as Record<string, string>));


/** Left segment span (inclusive of current if splitting) */
const leftSpan = computed(() => {
  if (!shouldSplit.value) return totalCols.value;
  const { start } = position.value;
  const cur = currentDatePosition.value;
  if (cur - start < 0) return 0;
  const span = Math.max(1, (cur - start + 1));
  return Math.min(totalCols.value, span);
});

const rightSpan = computed(() => Math.max(0, totalCols.value - leftSpan.value));

// Only the right segment exists now and carries the stripes image
const segStripeStyle = computed(() => ({
  // to fix
  gridColumn: `${leftSpan.value + 1} / span ${Math.max(0, rightSpan.value)}`,
  backgroundImage: STRIPES_BG,
  backgroundRepeat: "repeat",
  backgroundSize: "auto"
} as Record<string, string>));

const showStripe = computed(() => shouldSplit.value && rightSpan.value > 0);

const emit = defineEmits<{
  (e: "click", payload: ColorizedHistory): void
}>();

const onclick = () => emit("click", props.history);
</script>

<template>
  <!-- Single element button with primary background -->
  <button
    type="button"
    class="tw:relative tw:rounded-lg tw:my-1 tw:shadow-xs tw:text-xs tw:overflow-visible tw:flex tw:items-center tw:content-start"
    :style="buttonStyle"
    @click.prevent="onclick"
    :title="historyTitle"
    :aria-label="historyTitle"
  >
    <!-- Background grid: only the right part is overlaid with stripes -->
    <span class="tw:absolute tw:inset-0 tw:grid tw:rounded-lg tw:overflow-hidden tw:z-0"
      :style="bgGridStyle" aria-hidden="true">
      <span v-if="showStripe" :style="segStripeStyle"></span>
    </span>

    <!-- Foreground content overlays above backgrounds -->
    <span class="tw:relative tw:z-10 tw:inline-flex tw:items-center tw:pr-2 tw:pl-0.5 tw:py-0.5 tw:max-w-full">
      <span class="tw:rounded-full tw:w-4 tw:h-4 tw:flex-shrink-0 tw:text-white tw:flex tw:justify-center tw:items-center tw:mr-1"
            :style="{
              backgroundColor: iconBackgroundColor,
            }">
        <template v-if="props.history?.type === HistoryType.Booking">
          <CalendarRangeIcon class="tw:w-2.5 tw:h-2.5 tw:m-auto" />
        </template>
        <template v-if="props.history?.type === HistoryType.Allotment">
          <CalendarCogIcon class="tw:w-2.5 tw:h-2.5 tw:m-auto" />
        </template>
        <template v-if="props.history?.type === HistoryType.ProductTest">
          <PlayIcon class="tw:w-2.5 tw:h-2.5 tw:m-auto" />
        </template>
      </span>
      <span class="tw:whitespace-nowrap tw:overflow-hidden tw:text-ellipsis tw:truncate tw:max-w-full">
        {{ historyTitle }}
      </span>
    </span>
  </button>
</template>
